import { useEffect, useRef, useState, useCallback } from "react";
import {
  MessageSquare,
  X,
  Send,
  Loader2,
  Sparkles,
  ExternalLink,
  ShieldCheck,
  ChevronDown,
  Check,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthModal from "./AuthModal";
import { getChatSummary, getMessages, askQuestion } from "../lib/chat";
import { formatApiError } from "../lib/authApi";

const SELECTED_KEY = "kyp_floating_chat_upload_id";

/**
 * FloatingChat — a docked chat bubble that stays fixed to the bottom-right
 * as the user scrolls. Accepts either a single `uploadId` (locked context,
 * e.g. on PolicyAnalysis) or a list of `policies` (multi-policy picker,
 * e.g. on Dashboard). Preserves the login gate and grounded-chat API.
 */
export default function FloatingChat({ uploadId, policies, ready = true }) {
  const { user, loading: authLoading } = useAuth();
  const locked = !!uploadId;

  // Available policies for the picker (empty when locked).
  const list = locked ? [] : (policies || []).filter((p) => p && p.id);

  // Selected upload id (defaults to prop, or persisted, or first policy).
  const initialSelected = () => {
    if (locked) return uploadId;
    if (list.length === 0) return null;
    try {
      const saved = localStorage.getItem(SELECTED_KEY);
      if (saved && list.some((p) => p.id === saved)) return saved;
    } catch (e) {}
    return list[0].id;
  };
  const [selectedId, setSelectedId] = useState(initialSelected);

  // Reconcile when the parent's policies list changes underneath us.
  useEffect(() => {
    if (locked) {
      setSelectedId(uploadId);
      return;
    }
    if (list.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !list.some((p) => p.id === selectedId)) {
      setSelectedId(list[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locked, uploadId, list.map((p) => p.id).join(",")]);

  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const pickerRef = useRef(null);

  const handleOpen = () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    setOpen(true);
  };

  const activeId = locked ? uploadId : selectedId;

  const load = useCallback(async () => {
    if (!activeId || !user) return;
    setLoading(true);
    setError("");
    setMessages([]);
    setSummary(null);
    try {
      const [s, m] = await Promise.all([
        getChatSummary(activeId),
        getMessages(activeId),
      ]);
      setSummary(s);
      setMessages(m);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [activeId, user]);

  useEffect(() => {
    if (open && user && activeId) load();
  }, [open, user, activeId, load]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, busy, open]);

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickerOpen]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy || !activeId) return;
    setInput("");
    setBusy(true);
    const optimistic = {
      id: `opt-${Date.now()}`,
      role: "user",
      content: q,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    try {
      const res = await askQuestion(activeId, q);
      setMessages((m) => {
        const withoutOpt = m.filter((x) => x.id !== optimistic.id);
        return [...withoutOpt, res.question, res.answer];
      });
    } catch (e) {
      setMessages((m) => [
        ...m.filter((x) => x.id !== optimistic.id),
        {
          id: `err-${Date.now()}`,
          role: "assistant",
          content:
            "Sorry — I couldn't answer that just now. Please try again.",
          created_at: new Date().toISOString(),
          _error: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const pickPolicy = (id) => {
    setSelectedId(id);
    try {
      localStorage.setItem(SELECTED_KEY, id);
    } catch (e) {}
    setPickerOpen(false);
  };

  if (authLoading) return null;
  if (locked && !uploadId) return null;
  if (!locked && list.length === 0) return null;

  // Metadata for the active policy in picker mode
  const activePolicy = locked
    ? null
    : list.find((p) => p.id === activeId) || null;

  return (
    <>
      {/* Floating bubble — always visible */}
      {!open && (
        <button
          type="button"
          onClick={handleOpen}
          data-testid="floating-ask-ai-bubble"
          className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white pl-3 pr-4 py-3 shadow-xl shadow-blue-600/25 hover:shadow-2xl hover:shadow-blue-600/35 transition-shadow"
          aria-label="Ask AI about this policy"
        >
          <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
            <MessageSquare className="h-4 w-4" />
            {ready && (
              <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-blue-600" />
            )}
          </span>
          <span className="font-medium text-sm hidden sm:inline">
            Ask AI
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-4 right-4 z-40 flex flex-col w-[min(400px,calc(100vw-2rem))] h-[min(600px,calc(100vh-2rem))] rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/15 overflow-hidden"
          data-testid="floating-ask-ai-panel"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-gradient-to-b from-slate-50/70 to-white">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="h-7 w-7 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0">
                  <MessageSquare className="h-3.5 w-3.5" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-slate-900 text-sm truncate">
                    Ask AI
                  </div>

                  {/* Picker (multi-policy mode) OR fixed subtitle (locked mode) */}
                  {locked || list.length <= 1 ? (
                    <div className="text-[11px] text-slate-500 truncate">
                      Grounded in your uploaded policy
                    </div>
                  ) : (
                    <div className="relative" ref={pickerRef}>
                      <button
                        type="button"
                        onClick={() => setPickerOpen((v) => !v)}
                        data-testid="floating-policy-picker-toggle"
                        className="inline-flex items-center gap-1 max-w-full text-[11px] text-slate-600 hover:text-slate-900 rounded px-0.5 -mx-0.5"
                      >
                        <span className="truncate max-w-[220px]">
                          {activePolicy?.insurer || activePolicy?.filename || "Choose a policy"}
                        </span>
                        <ChevronDown className={`h-3 w-3 shrink-0 transition-transform ${pickerOpen ? "rotate-180" : ""}`} />
                      </button>
                      {pickerOpen && (
                        <div
                          className="absolute left-0 mt-1 z-50 min-w-[260px] max-h-[240px] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl"
                          data-testid="floating-policy-picker-menu"
                        >
                          {list.map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => pickPolicy(p.id)}
                              data-testid={`floating-policy-option-${p.id}`}
                              className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-slate-50 ${
                                p.id === activeId ? "bg-blue-50/60" : ""
                              }`}
                            >
                              <span className="flex-1 min-w-0">
                                <span className="block text-sm font-medium text-slate-900 truncate">
                                  {p.insurer || p.filename || p.id.slice(0, 8)}
                                </span>
                                {p.vehicle && (
                                  <span className="block text-xs text-slate-500 truncate">
                                    {p.vehicle}
                                  </span>
                                )}
                              </span>
                              {p.id === activeId && (
                                <Check className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {activeId && (
                <Link
                  to={`/chat/${activeId}`}
                  className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                  title="Open full chat"
                  data-testid="floating-ask-ai-expand"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                data-testid="floating-ask-ai-close"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 bg-slate-50/30"
            data-testid="floating-ask-ai-body"
          >
            {loading ? (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading…
              </div>
            ) : error ? (
              <div className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl p-3">
                {error}
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center text-center py-4">
                <div className="h-11 w-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-600/20">
                  <Sparkles className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-display text-base font-semibold text-slate-900">
                  Ask anything about this policy
                </h3>
                <p className="mt-1 text-xs text-slate-500 max-w-[260px]">
                  Answers come only from your uploaded document.
                </p>
                {summary?.suggested_questions?.length > 0 && (
                  <div className="mt-4 w-full space-y-2" data-testid="floating-suggested-questions">
                    {summary.suggested_questions.slice(0, 3).map((q, i) => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        data-testid={`floating-suggested-q-${i}`}
                        className="w-full text-left bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 rounded-xl px-3 py-2 text-xs text-slate-700"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${
                      m.role === "user" ? "justify-end" : "justify-start"
                    }`}
                    data-testid={`floating-msg-${m.role}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-blue-600 text-white rounded-br-sm"
                          : m._error
                          ? "bg-red-50 text-red-800 border border-red-100 rounded-bl-sm"
                          : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex justify-start" data-testid="floating-chat-thinking">
                    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3 py-2 text-xs text-slate-500 inline-flex items-center gap-2">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Reading policy…
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="p-3 border-t border-slate-100 bg-white"
            data-testid="floating-chat-form"
          >
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-full pl-3 pr-1 py-1 focus-within:border-blue-400 focus-within:bg-white">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about coverage, exclusions…"
                className="flex-1 bg-transparent outline-none text-xs text-slate-800 placeholder:text-slate-400 py-2"
                data-testid="floating-chat-input"
                disabled={busy || loading || !!error}
              />
              <button
                type="submit"
                disabled={!input.trim() || busy || loading || !!error}
                data-testid="floating-chat-send-btn"
                className="inline-flex items-center justify-center h-8 w-8 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-full"
                aria-label="Send"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
            <p className="mt-2 text-[10px] text-slate-400 text-center flex items-center justify-center gap-1">
              <ShieldCheck className="h-3 w-3" />
              AI-generated — verify with your insurer.
            </p>
          </form>
        </div>
      )}

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        onSuccess={() => {
          setAuthOpen(false);
          setOpen(true);
        }}
        headline="Log in to chat with this policy"
        subheadline="We'll link this policy to your account so you can pick up right here."
      />
    </>
  );
}
